/**
 * COMMENT_ON_PHOTO task template - shows photo with comment prompt
 *
 * @module pages/tasks/components/templates/CommentOnPhotoTaskTemplate
 */

import { Text } from '@/components/atoms/Text';
import { TaskDebugPanel } from '@/components/composed/TaskDebugPanel';
import { TargetPreview } from '../previews';
import { TaskActions } from './TaskActions';
import { getStatusColor } from '../../utils';
import type { TaskTemplateProps } from '../../types';

export function CommentOnPhotoTaskTemplate({ task, onNavigate, onComplete, onDismiss, onDelete, isUpdating, debugMode }: TaskTemplateProps) {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <span className="text-sm font-medium text-foreground">Leave a Comment</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
              {task.status}
            </span>
            {task.hasProgress && task.progressPercent !== null && (
              <span className="text-xs text-muted">
                {task.progressPercent}% complete
              </span>
            )}
          </div>

          {/* Description if available */}
          {task.description && (
            <Text className="text-sm mb-3 line-clamp-2">{task.description}</Text>
          )}

          {/* Target Preview */}
          <div className="mb-3">
            <TargetPreview task={task} />
          </div>

          {/* Progress bar if applicable */}
          {task.hasProgress && task.progressRequired && (
            <div className="mb-3">
              <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${task.progressPercent ?? 0}%` }}
                />
              </div>
              <Text className="text-xs text-muted mt-1">
                {task.progressCurrent} / {task.progressRequired} comments
              </Text>
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-muted">
            <span>{new Date(task.createdAt).toLocaleTimeString()}</span>
          </div>

          {/* Debug Panel */}
          {debugMode && <TaskDebugPanel task={task} />}
        </div>

        {/* Actions */}
        <TaskActions
          task={task}
          onNavigate={onNavigate}
          onComplete={onComplete}
          onDismiss={onDismiss}
          onDelete={onDelete}
          isUpdating={isUpdating}
        />
      </div>
    </div>
  );
}
